import { Link } from "react-router-dom";
import { AuthShow as Show } from "@/components/auth/AuthShow";
import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  QrCode, Utensils, Bike, PhoneIncoming, Printer, Wallet, Volume2, CreditCard, LayoutGrid,
  CheckCircle2, ArrowRight, MapPin, BellRing, Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo, SupportButtons } from "@/components/Brand";
import { InstallAppButton } from "@/components/Download";
import { PLATFORM_INFO, SUPPORT_TEL, SUPPORT_PHONE } from "@/lib/api";

const NAV = [["ozellikler", "Özellikler"], ["entegrasyonlar", "Entegrasyonlar"], ["uygulamalar", "Mobil Uygulamalar"], ["indir", "İndir"], ["fiyat", "Fiyat"], ["sss", "SSS"], ["destek", "Destek"]];

const FEATURES = [
  [LayoutGrid, "Masa Sistemi", "Masa açın, sipariş ekleyin, hesabı nakit, POS veya kredi kartıyla kapatın."],
  [QrCode, "QR Kod Sistemi", "Her masaya özel QR kod. Müşteri menüyü görür, masadan sipariş verir."],
  [Utensils, "Fotoğraflı Menü", "İstediğiniz kategori ve ürünü ekleyin, ürün fotoğraflarını yükleyin."],
  [Wallet, "Kasa & Giderler", "Günlük gelir, yakıt, kira, malzeme gibi tüm giderleri ekleyin ve düzenleyin."],
  [Printer, "Otomatik Fiş", "Sipariş geldiği anda fiş otomatik yazdırılır. 58mm / 80mm termal yazıcı."],
  [Volume2, "Yüksek Sesli Bildirim", "Yeni siparişte güçlü alarm sesi. Hiçbir sipariş kaçmaz."],
  [CreditCard, "Ödeme Yöntemleri", "Nakit, POS cihazı ve kredi kartı ödemelerini ayrı ayrı takip edin."],
  [Bike, "Kurye Takip", "Siparişi tek tıkla kuryeye gönderin, kuryenin konumunu haritada izleyin."],
  [PhoneIncoming, "Caller ID", "Arayan müşterinin adı, adresi ve geçmiş siparişleri anında ekranda."],
];

const EASE = [0.16, 1, 0.3, 1];
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, delay, ease: EASE },
});

function Header() {
  return (
    <motion.header initial={{ y: -70, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 }} className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#0F1F45]/10">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
        <a href="#anasayfa" className="flex items-center gap-2" data-testid="nav-home-link">
          <Logo className="h-11 w-11" />
          <span className="font-display text-xl font-extrabold text-[#0F1F45]">Gazi <span className="text-[#FF6B0A]">Sepet</span></span>
        </a>
        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-600">
          {NAV.map(([id, l]) => <a key={id} href={`#${id}`} className="hover:text-[#FF6B0A] transition-colors" data-testid={`nav-${id}-link`}>{l}</a>)}
        </nav>
        <div className="flex items-center gap-2">
          <a href={SUPPORT_TEL} className="hidden md:block text-sm font-semibold text-[#0F1F45]" data-testid="nav-phone-link">{SUPPORT_PHONE}</a>
          <Show when="signed-in">
            <Link to="/panel" data-testid="nav-panel-btn"><Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]">Panelime Git</Button></Link>
          </Show>
          <Show when="signed-out">
            <Link to="/sign-in" data-testid="nav-login-btn"><Button variant="ghost" className="rounded-full">Giriş</Button></Link>
            <Link to="/sign-up" data-testid="nav-signup-btn"><Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]">Kayıt Ol</Button></Link>
          </Show>
        </div>
      </div>
    </motion.header>
  );
}

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden pb-1 ${className}`}>
      <motion.span className="block will-change-transform" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay, ease: EASE }}>
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const panelY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const rows = [["Masa 4", "3 ürün • Hazırlanıyor", "bg-emerald-500"], ["Yemeksepeti #128", "Kuryeye gönderildi", "bg-[#EA002A]"], ["Trendyol Go #77", "Yeni sipariş", "bg-[#F27A1A]"], ["QR Masa 9", "Müşteri siparişi", "bg-violet-500"]];
  return (
    <section id="anasayfa" ref={ref} className="relative overflow-hidden bg-[#0F1F45] grain text-white">
      <motion.div style={{ y: glowY }} className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#FF6B0A]/25 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 py-20 lg:py-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
        <div>
          <motion.span {...reveal(0.15)} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#FFB27A]">
            <BellRing className="h-3.5 w-3.5" /> Restoran, cafe ve paket servis için tek sistem
          </motion.span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            <MaskedLine delay={0.25}>Adisyon, QR menü ve</MaskedLine>
            <MaskedLine delay={0.4}>paket servis <span className="text-[#FF6B0A]">tek panelde.</span></MaskedLine>
          </h1>
          <motion.p {...reveal(0.65)} className="mt-6 text-base md:text-lg text-white/70 max-w-xl">
            Gazi Sepet; Yemeksepeti, Trendyol Go Yemek ve Migros Yemek siparişlerini, masa ve QR siparişlerini, kurye takibini, kasa ve giderleri tek ekranda toplar.
          </motion.p>
          <motion.div {...reveal(0.8)} className="mt-9 flex flex-wrap gap-3">
            <Link to="/sign-up" data-testid="hero-start-btn"><Button size="lg" className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-2 h-12 px-7 transition-transform hover:scale-[1.04] active:scale-95">Hemen Başla <ArrowRight className="h-4 w-4" /></Button></Link>
            <a href="#fiyat" data-testid="hero-pricing-btn"><Button size="lg" variant="outline" className="rounded-full h-12 px-7 bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">Yıllık 6.000 TL</Button></a>
          </motion.div>
          <motion.div {...reveal(0.95)} className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
            {["QR menü & sipariş", "Otomatik fiş", "Kurye uygulaması", "Caller ID"].map((t) => <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#FF6B0A]" />{t}</span>)}
          </motion.div>
        </div>
        <motion.div style={{ y: panelY }} initial={{ opacity: 0, y: 60, rotate: 2 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 1.1, delay: 0.5, ease: EASE }} className="rounded-3xl bg-white/[0.06] border border-white/10 backdrop-blur-xl p-5 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div><p className="font-display font-bold">Canlı Sipariş Paneli</p><p className="text-xs text-white/50">Masa, paket, QR ve platformlar</p></div>
            <span className="rounded-full bg-emerald-500/20 text-emerald-300 text-xs px-3 py-1 font-bold">CANLI</span>
          </div>
          <div className="space-y-2.5">
            {rows.map(([a, b, c], i) => (
              <motion.div key={a} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 + i * 0.18, duration: 0.7, ease: EASE }} className={`flex items-center gap-3 rounded-2xl bg-white/[0.07] p-3.5 ${i === 2 ? "pulse-new" : ""}`}>
                <span className={`h-9 w-9 rounded-xl ${c}`} />
                <div className="flex-1"><p className="font-semibold text-sm">{a}</p><p className="text-xs text-white/50">{b}</p></div>
              </motion.div>
            ))}
          </div>
          <motion.img initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.5, duration: 1, ease: EASE }} src="/img/food.jpg" alt="Restoran yemekleri" className="mt-4 h-40 w-full object-cover rounded-2xl" />
        </motion.div>
      </div>
    </section>
  );
}

const MARQUEE_ITEMS = ["Sipariş düştü — Masa 4", "Yemeksepeti #128 kuryede", "Fiş yazdırıldı", "Trendyol Go #77 yeni sipariş", "QR Masa 9 siparişi", "Kurye yola çıktı", "Kasa: ₺4.850 bugün", "Gelen arama — Ahmet Y."];

function Marquee() {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="bg-[#FF6B0A] overflow-hidden py-3 border-y border-[#0F1F45]/20" data-testid="landing-marquee">
      <div className="marquee-track flex w-max items-center gap-10">
        {row.map((t, i) => (
          <span key={i} className="flex items-center gap-3 text-white font-display font-semibold text-sm whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-white/70" />{t}
          </span>
        ))}
      </div>
    </div>
  );
}

function Features() {
  return (
    <section id="ozellikler" className="max-w-7xl mx-auto px-5 py-24">
      <motion.p {...reveal(0)} className="text-xs uppercase tracking-widest font-bold text-[#FF6B0A]">Özellikler</motion.p>
      <motion.h2 {...reveal(0.1)} className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold max-w-2xl">Restoran operasyonu için ihtiyaç duyduğunuz her şey.</motion.h2>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map(([Icon, t, d], i) => (
          <motion.div key={t} {...reveal(0.06 * i)} className="group rounded-3xl border border-[#0F1F45]/10 bg-white p-7 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#0F1F45]/5 transition-[transform,box-shadow] duration-300" data-testid={`feature-card-${i}`}>
            <div className="h-12 w-12 rounded-2xl bg-[#FF6B0A]/10 text-[#FF6B0A] grid place-items-center group-hover:bg-[#FF6B0A] group-hover:text-white group-hover:scale-110 transition-[background-color,color,transform] duration-300"><Icon className="h-6 w-6" /></div>
            <h3 className="mt-5 text-xl font-semibold">{t}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function IntegrationsSec() {
  return (
    <section id="entegrasyonlar" className="bg-white border-y border-[#0F1F45]/10">
      <div className="max-w-7xl mx-auto px-5 py-24 grid lg:grid-cols-[1fr_1.3fr] gap-12 items-center">
        <div>
          <motion.p {...reveal(0)} className="text-xs uppercase tracking-widest font-bold text-[#FF6B0A]">Sipariş Yöntemleri</motion.p>
          <motion.h2 {...reveal(0.1)} className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold">Tüm siparişleriniz tek ekrana düşer.</motion.h2>
          <motion.p {...reveal(0.2)} className="mt-4 text-slate-600">Telefon, gel-al, masa ve QR siparişlerinin yanında yemek platformlarının siparişleri de aynı panelde. Her platform için yıllık aktivasyon.</motion.p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {Object.entries(PLATFORM_INFO).map(([k, p], i) => (
            <motion.div key={k} {...reveal(0.08 * i)} className="rounded-3xl p-6 text-white relative overflow-hidden" style={{ background: p.color }} data-testid={`landing-platform-${k}`}>
              <span className="font-display text-5xl font-black opacity-30 absolute -right-1 -bottom-3">{p.letter}</span>
              <p className="font-display font-bold text-lg">{p.name}</p>
              <p className="text-xs mt-1 text-white/80">Yıllık aktivasyon</p>
            </motion.div>
          ))}
          {[["Telefon & Gel-Al", "Hızlı sipariş"], ["Masa", "Salon servisi"], ["QR Menü", "Masadan sipariş"]].map(([a, b], i) => (
            <motion.div key={a} {...reveal(0.24 + 0.08 * i)} className="rounded-3xl p-6 bg-slate-50 border border-[#0F1F45]/10"><p className="font-display font-bold">{a}</p><p className="text-xs mt-1 text-slate-500">{b}</p></motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Apps() {
  return (
    <section id="uygulamalar" className="max-w-7xl mx-auto px-5 py-24 grid lg:grid-cols-2 gap-8">
      <motion.div {...reveal(0)} className="rounded-3xl bg-[#0F1F45] text-white p-8 lg:p-10 relative overflow-hidden grain">
        <Bike className="h-10 w-10 text-[#FF6B0A]" />
        <h3 className="mt-5 text-2xl font-bold">Kurye Takip Uygulaması</h3>
        <p className="mt-3 text-white/70 text-sm leading-relaxed">Panelden gönderdiğiniz sipariş anında kuryenin telefonuna düşer. Kurye haritada müşteri konumunu görür, tek dokunuşla müşteriyi arar, yol tarifi alır ve teslimatı onaylar.</p>
        <ul className="mt-6 space-y-2 text-sm">
          {["Harita ve canlı konum", "Müşteri telefon numarası ve arama", "Yola çıktım / teslim ettim", "Telefona yüklenebilir"].map((t) => <li key={t} className="flex gap-2"><MapPin className="h-4 w-4 text-[#FF6B0A]" />{t}</li>)}
        </ul>
        <img src="/img/courier.jpg" alt="Kurye" className="mt-8 h-44 w-full object-cover rounded-2xl" />
      </motion.div>
      <motion.div {...reveal(0.12)} className="rounded-3xl bg-[#FF6B0A] text-white p-8 lg:p-10 relative overflow-hidden">
        <PhoneIncoming className="h-10 w-10" />
        <h3 className="mt-5 text-2xl font-bold">Caller ID Uygulaması</h3>
        <p className="mt-3 text-white/85 text-sm leading-relaxed">Arayan müşterinin numarası girildiği anda adı, adresi ve geçmiş siparişleri görünür; panelde de bildirim açılır. Tek dokunuşla siparişi oluşturun.</p>
        <ul className="mt-6 space-y-2 text-sm">
          {["Müşteri geçmişi ve adresi", "Panelde anlık arama bildirimi", "Aramadan sipariş oluşturma", "Telefona yüklenebilir"].map((t) => <li key={t} className="flex gap-2"><Smartphone className="h-4 w-4" />{t}</li>)}
        </ul>
        <div className="mt-8 rounded-2xl bg-white text-[#0F1F45] p-5 shadow-xl">
          <p className="text-xs text-slate-500">Gelen arama</p>
          <p className="font-mono-num text-2xl font-bold mt-1">0532 *** ** 45</p>
          <p className="text-sm mt-2">Ahmet Y. • 12 sipariş • Gazi Mah.</p>
        </div>
      </motion.div>
    </section>
  );
}

function Pricing() {
  const items = ["Masa sistemi ve QR kod sistemi", "Fotoğraflı menü yönetimi", "Yemeksepeti, Trendyol Go Yemek, Migros Yemek aktivasyonu", "Kasa ve gider yönetimi", "Kurye takip uygulaması", "Caller ID uygulaması", "Otomatik fiş ve yazıcı menüsü", "Nakit / POS / kredi kartı takibi", "WhatsApp ve telefon destek"];
  return (
    <section id="fiyat" className="bg-white border-y border-[#0F1F45]/10">
      <div className="max-w-5xl mx-auto px-5 py-24">
        <motion.p {...reveal(0)} className="text-xs uppercase tracking-widest font-bold text-[#FF6B0A] text-center">Teklif</motion.p>
        <motion.h2 {...reveal(0.1)} className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-center">Tek paket, her şey dahil.</motion.h2>
        <motion.div {...reveal(0.2)} className="mt-12 rounded-[2rem] border-2 border-[#FF6B0A] p-8 md:p-12 grid md:grid-cols-2 gap-10 shadow-2xl shadow-[#FF6B0A]/10" data-testid="pricing-card">
          <div>
            <p className="font-display font-bold text-lg">Gazi Sepet Tam Paket</p>
            <p className="mt-4 flex items-end gap-2"><span className="font-display text-6xl font-black text-[#0F1F45]" data-testid="pricing-amount">6.000 TL</span><span className="text-slate-500 mb-2">/ yıllık</span></p>
            <p className="mt-3 text-sm text-slate-600">Tüm modüller ve entegrasyonlar <b>yıllık aktivasyon</b> ile açılır. Aktivasyon için bize WhatsApp veya telefonla ulaşın.</p>
            <div className="mt-8"><SupportButtons prefix="pricing" /></div>
          </div>
          <ul className="space-y-3">
            {items.map((t) => <li key={t} className="flex gap-3 text-sm"><CheckCircle2 className="h-5 w-5 text-[#FF6B0A] shrink-0" />{t} <span className="ml-auto text-xs text-slate-400 whitespace-nowrap">yıllık</span></li>)}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

const FAQ = [
  ["Yemeksepeti, Trendyol Go ve Migros siparişleri nasıl gelir?", "Her platform yıllık aktivasyonla açılır. Panelde platformu etkinleştirip bayi API bilgilerinizi girersiniz; siparişler sipariş ekranına düşer. Ayrıca platform siparişlerini elle de girebilirsiniz."],
  ["Kurye ve Caller ID uygulamaları telefona nasıl yüklenir?", "Uygulama adresini telefonunuzun tarayıcısında açıp “Ana ekrana ekle / Telefona Yükle” seçeneğine dokunmanız yeterli. Uygulama simgesiyle ana ekranınıza eklenir."],
  ["Fişler otomatik yazdırılır mı?", "Evet. Yazıcı menüsünden otomatik yazdırmayı açtığınızda yeni sipariş geldiği anda fiş yazdırılır ve yüksek sesli bildirim çalar."],
  ["Ödeme yöntemlerini ayrı görebilir miyim?", "Kasa ekranında nakit, POS cihazı ve kredi kartı gelirleri ayrı ayrı; giderleriniz de kategori bazında görünür."],
  ["Fiyat nedir?", "Tüm modüller dahil yıllık 6.000 TL."],
];

export default function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 0.9 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="bg-slate-50 text-[#0F1F45]" data-testid="landing-page">
      <Header />
      <Hero />
      <Marquee />
      <Features />
      <IntegrationsSec />
      <Apps />
      <Pricing />
      <section id="indir" className="max-w-7xl mx-auto px-5 py-24">
        <motion.p {...reveal(0)} className="text-xs uppercase tracking-widest font-bold text-[#FF6B0A]">İndir</motion.p>
        <motion.h2 {...reveal(0.1)} className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold">Siteyi program gibi kullanın.</motion.h2>
        <motion.div {...reveal(0.15)} className="mt-12 rounded-3xl bg-[#0F1F45] grain text-white p-8 lg:p-12 relative overflow-hidden grid md:grid-cols-[1.4fr_auto] gap-8 items-center" data-testid="download-install-card">
          <div>
            <p className="font-display text-2xl font-bold">Bilgisayara / Telefona Program Olarak Yükle</p>
            <p className="mt-3 text-white/70 text-sm leading-relaxed max-w-xl">Gazi Sepet'i masaüstünüze veya telefonunuzun ana ekranına program kısayolu olarak ekleyin; tarayıcısız, tam ekran çalışır. Panel, kurye ve Caller ID uygulamaları da ayrı ayrı yüklenebilir.</p>
          </div>
          <div className="md:justify-self-end"><InstallAppButton /></div>
        </motion.div>
      </section>

      <section id="sss" className="max-w-3xl mx-auto px-5 py-24">
        <motion.p {...reveal(0)} className="text-xs uppercase tracking-widest font-bold text-[#FF6B0A]">Sık Sorulanlar</motion.p>
        <motion.h2 {...reveal(0.1)} className="mt-3 text-2xl sm:text-3xl font-bold">Merak edilenler</motion.h2>
        <motion.div {...reveal(0.2)}>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`f${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-semibold">{q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>
      <section id="destek" className="bg-[#0F1F45] text-white grain relative">
        <div className="max-w-7xl mx-auto px-5 py-20 grid md:grid-cols-2 gap-10 items-center">
          <motion.div {...reveal(0)}>
            <p className="text-xs uppercase tracking-widest font-bold text-[#FF6B0A]">Destek Merkezi</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold">Kurulum, aktivasyon ve destek için bize ulaşın.</h2>
            <p className="mt-4 text-white/70">WhatsApp üzerinden yazın veya doğrudan arayın: <b className="text-white">{SUPPORT_PHONE}</b></p>
          </motion.div>
          <motion.div {...reveal(0.15)} className="md:justify-self-end"><SupportButtons prefix="landing-support" /></motion.div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">© {new Date().getFullYear()} Gazi Sepet. Tüm hakları saklıdır.</div>
      </section>
    </div>
  );
}
